import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { InfoButton } from '../../components/connectedForm/Button';
import hasValidationErrors from '../../components/connectedForm/utils/hasValidationErrors';
import createPostingPreview from '../../views/nominal/NominalPostingPreviewForm';
import createPostedTransactions from '../../views/nominal/PostedTransactionsForm';
import SalesReceiptPostMutation from '../../graphql/mutations/salesLedger/SalesReceiptPostMutation.graphql';
import SalesReceiptAutoAllocateMutation from '../../graphql/mutations/salesLedger/SalesReceiptAutoAllocateMutation.graphql';
import SalesReceiptReverseAllocationsMutation from '../../graphql/mutations/salesLedger/SalesReceiptReverseAllocationsMutation.graphql';
import SalesReceiptPostingPreview from '../../graphql/entities/salesLedger/SalesReceiptPostingPreview';
import SalesReceiptTransactions from '../../graphql/entities/salesLedger/SalesReceiptTransactions';
import { PostSelectButton } from '../../components/SelectButton';
import getClientMutationId from '../../utils/getClientMutationId';
import { IFormAction } from '../../@types/entityTypes';
import {
  AlertType,
  ICustomer,
  DocumentStatus,
  IFilterInput,
  ISalesReceipt,
  ISalesReceiptAutoAllocateMutationResult,
  ISalesReceiptAutoAllocateMutationVariables,
  ISalesReceiptPostMutationResult,
  ISalesReceiptPostMutationVariables,
  ISalesReceiptReverseAllocationsMutationResult,
  ISalesReceiptReverseAllocationsMutationVariables,
  PaymentReceiptAutoAllocateSequence,
} from '../../graphql/schema/p4types';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useMutation } from '@apollo/client';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useCustomerConfig } from '../../utils/FetchConfig';
import { usePreviewPostingsAction } from '../usePreviewPostingsAction';
import { useViewTransactionsAction } from '../useViewTransactionsAction';
import { useCustomerAccountStatusUpdate } from './useCustomerAccountStatusUpdate';

enum SalesReceiptActions {
  AutoAllocate = 'SALES_LEDGER_RECEIPTS_AUTO_ALLOCATE',
  PayInFull = 'SALES_LEDGER_RECEIPTS_PAY_IN_FULL',
  PreviewPostings = 'SALES_LEDGER_RECEIPTS_PREVIEW_POSTINGS',
  ReverseAllocations = 'SALES_LEDGER_RECEIPTS_REVERSE_ALLOCATIONS',
  ViewTransactions = 'SALES_LEDGER_RECEIPTS_VIEW_TRANSACTIONS',
  PostReceipt = 'SALES_LEDGER_RECEIPTS_POST_RECEIPT',
}

export const formActionsForPermissions = [
  { actionReference: SalesReceiptActions.AutoAllocate },
  { actionReference: SalesReceiptActions.PayInFull },
  { actionReference: SalesReceiptActions.PreviewPostings },
  { actionReference: SalesReceiptActions.ReverseAllocations },
  { actionReference: SalesReceiptActions.ViewTransactions },
  { actionReference: SalesReceiptActions.PostReceipt },
];

const SALES_RECEIPT_POSTING_PREVIEW = createPostingPreview(SalesReceiptPostingPreview);
const SALES_RECEIPT_POSTED_TRANSACTIONS = createPostedTransactions(SalesReceiptTransactions);

const canBePosted = (receipt: ISalesReceipt): boolean => {
  if (receipt && receipt.documentStatus) {
    return receipt.documentStatus === DocumentStatus.Draft || receipt.documentStatus === DocumentStatus.Released;
  }
  return false;
};

const useAutoAllocateAction = (
  payInFull: boolean,
  salesTransactionFilters: Array<IFilterInput>
): [action: IFormAction<SalesReceiptActions>, modals: ReactNode] => {
  const [performSave, confirmationPrompt] = usePerformSave();
  const handleQueryError = useComponentQueryErrorHandling();
  const [runAutoAllocate] = useMutation<
    ISalesReceiptAutoAllocateMutationResult,
    ISalesReceiptAutoAllocateMutationVariables
  >(SalesReceiptAutoAllocateMutation, {
    onError: handleQueryError,
  });
  const { dispatchSimpleToast } = useP4ToastController();

  const connectedForm = useContext(ConnectedFormContext);

  const onClickAutoAllocate = useCallback(() => {
    performSave({
      submitMeta: { suppressAfterSubmit: true },
      onSaveComplete: ({ result }: { result: ISalesReceipt }) => {
        if (hasValidationErrors(connectedForm)) {
          return;
        }

        runAutoAllocate({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              id: result.id,
              payInFull: payInFull,
              salesTransactionFilters: salesTransactionFilters,
            },
          },
        }).then((value) => {
          connectedForm.change(false, value!.data!.salesReceiptAutoAllocate!.result);
          dispatchSimpleToast(
            payInFull ? 'Pay in full' : 'Auto Allocate',
            'Receipt automatically allocated',
            AlertType.Success
          );
        });
      },
    });
  }, [connectedForm, dispatchSimpleToast, payInFull, performSave, runAutoAllocate, salesTransactionFilters]);

  const customerConfig = useCustomerConfig();
  let tooltip = `Automatically ${
    payInFull ? 'pay (in full) all outstanding transactions' : 'allocate the currently filtered'
  } in order of payment-due-date`;
  switch (customerConfig?.autoAllocateSequence) {
    case PaymentReceiptAutoAllocateSequence.CreditsFirst:
      tooltip += ' (Credits First)';
      break;
    case PaymentReceiptAutoAllocateSequence.ExcludeCredits:
      tooltip += ' (Credits Excluded)';
      break;
  }

  return [
    {
      key: payInFull ? 'payInFull' : 'autoAllocate',
      text: payInFull ? 'Pay in full' : 'Auto Allocate',
      actionReference: payInFull ? SalesReceiptActions.PayInFull : SalesReceiptActions.AutoAllocate,
      onClick: onClickAutoAllocate,
      condition: canBePosted,
      iconProps: { iconName: 'save' },
      tooltip: tooltip,
    },
    confirmationPrompt,
  ];
};

const useReverseAllocationsAction = (): [action: IFormAction<SalesReceiptActions>, modals: ReactNode] => {
  const [performSave, confirmationPrompt] = usePerformSave();
  const connectedForm = useContext(ConnectedFormContext);
  const handleQueryError = useComponentQueryErrorHandling();
  const [allocationsReversedOrInProgress, setAllocationsReversedOrInProgress] = useState<boolean>(false);

  const [runReverseAllocations] = useMutation<
    ISalesReceiptReverseAllocationsMutationResult,
    ISalesReceiptReverseAllocationsMutationVariables
  >(SalesReceiptReverseAllocationsMutation, { onError: handleQueryError });
  const { dispatchSimpleToast } = useP4ToastController();

  const onClickReverse = useCallback(() => {
    setAllocationsReversedOrInProgress(true);
    performSave({
      submitMeta: { suppressAfterSubmit: true },
      onSaveComplete: ({ result }) => {
        runReverseAllocations({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              id: result.id,
            },
          },
        })
          .then((reverseResponse) => {
            if (reverseResponse.data) {
              setAllocationsReversedOrInProgress(true);
              connectedForm.afterSubmit?.();
              dispatchSimpleToast('Reverse Allocations', 'Allocations reversed', AlertType.Success);
            }
          })
          .catch(() => {
            setAllocationsReversedOrInProgress(false);
          });
      },
      onSaveFailed: () => {
        setAllocationsReversedOrInProgress(false);
      },
    });
  }, [connectedForm, dispatchSimpleToast, performSave, runReverseAllocations]);

  const canBeReversed = useCallback(
    (receipt: ISalesReceipt) => {
      if (receipt && receipt.documentStatus && receipt.documentStatus === DocumentStatus.Posted) {
        return !!(
          receipt.salesAllocation &&
          receipt.salesAllocation.documentStatus === DocumentStatus.Posted &&
          !receipt.foreignCurrency &&
          !allocationsReversedOrInProgress
        );
      }
      return false;
    },
    [allocationsReversedOrInProgress]
  );

  const reverseAllocationsAction = useMemo<IFormAction<SalesReceiptActions>>(
    () => ({
      key: 'reverseAllocations',
      text: 'Reverse allocations',
      actionReference: SalesReceiptActions.ReverseAllocations,
      onClick: onClickReverse,
      individualButton: true,
      individualButtonComponent: InfoButton,
      condition: canBeReversed,
      iconProps: { iconName: 'save' },
    }),
    [canBeReversed, onClickReverse]
  );

  return [reverseAllocationsAction, confirmationPrompt];
};

const usePostReceiptAction = (): [action: IFormAction<SalesReceiptActions>, modals: ReactNode] => {
  const connectedForm = useContext(ConnectedFormContext);
  const [performSave, confirmationPrompt] = usePerformSave();
  const handleQueryError = useComponentQueryErrorHandling();
  const { dispatchSimpleToast } = useP4ToastController();
  const [runPostReceipt] = useMutation<ISalesReceiptPostMutationResult, ISalesReceiptPostMutationVariables>(
    SalesReceiptPostMutation,
    { onError: handleQueryError }
  );

  const customerConfig = useCustomerConfig();
  const triggerCustomerAccountStatusUpdate = useCustomerAccountStatusUpdate();

  const onClickPostReceipt = useCallback(() => {
    if (hasValidationErrors(connectedForm)) return;

    performSave({
      submitMeta: { suppressAfterSubmit: true },
      onSaveComplete: ({ result }) => {
        runPostReceipt({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              id: result.id,
            },
          },
        }).then((postResponse) => {
          if (postResponse.data) {
            dispatchSimpleToast('Post Receipt', 'Receipt Posted', AlertType.Success);
            connectedForm.afterSubmit?.();

            // If enabled in Customer Configuration, display a Prompt/Modal to update Account Status for the Customer.
            if (customerConfig?.reviewAccountStatusAfterSalesReceipt) {
              triggerCustomerAccountStatusUpdate(postResponse.data.salesReceiptPost!.result!.customer! as ICustomer);
            }
          }
        });
      },
    });
  }, [connectedForm, dispatchSimpleToast, performSave, runPostReceipt, triggerCustomerAccountStatusUpdate]);

  const postReceiptAction = useMemo<IFormAction<SalesReceiptActions>>(
    () => ({
      key: 'postReceipt',
      text: 'Post Receipt',
      onClick: onClickPostReceipt,
      actionReference: SalesReceiptActions.PostReceipt,
      individualButton: true,
      individualButtonComponent: PostSelectButton,
      condition: canBePosted,
      iconProps: { iconName: 'save' },
    }),
    [onClickPostReceipt]
  );

  return [postReceiptAction, confirmationPrompt];
};

export const useSalesReceiptActions: (parameters: {
  workspaceFilters: Array<IFilterInput>;
}) => [Array<IFormAction<SalesReceiptActions>>, ReactNode] = ({ workspaceFilters }) => {
  const [autoAllocateAction, autoAllocateModals] = useAutoAllocateAction(false, workspaceFilters);
  const [payInFullAction, payInFullModals] = useAutoAllocateAction(true, workspaceFilters);
  const [previewPostingsAction, previewPostingsModals] = usePreviewPostingsAction({
    actionReference: SalesReceiptActions.PreviewPostings,
    individualButton: false,
    postingPreview: SALES_RECEIPT_POSTING_PREVIEW,
  });
  const [reverseAllocationsAction, reverseAllocationsModals] = useReverseAllocationsAction();
  const [viewTransactionsAction, viewTransactionsModals] = useViewTransactionsAction({
    actionReference: SalesReceiptActions.ViewTransactions,
    individualButton: true,
    viewTransactions: SALES_RECEIPT_POSTED_TRANSACTIONS,
  });
  const [postReceipt, postReceiptModals] = usePostReceiptAction();

  const formActions: Array<IFormAction<SalesReceiptActions>> = [
    autoAllocateAction,
    payInFullAction,
    previewPostingsAction,
    reverseAllocationsAction,
    viewTransactionsAction,
    postReceipt,
  ];

  return [
    formActions,
    <>
      {autoAllocateModals}
      {payInFullModals}
      {previewPostingsModals}
      {reverseAllocationsModals}
      {viewTransactionsModals}
      {postReceiptModals}
    </>,
  ];
};
